import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Save, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminBrands() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", slug: "", description: "", logo_url: "", active: true });
    const [error, setError] = useState("");

    const load = async () => {
        const { data } = await api.get("/admin/brands");
        setItems(data || []);
    };
    useEffect(() => { load(); }, []);

    const startEdit = (it) => {
        setEditing(it.id);
        setForm({ name: it.name, slug: it.slug, description: it.description || "", logo_url: it.logo_url || "", active: it.active !== false });
    };
    const startNew = () => {
        setEditing("new");
        setForm({ name: "", slug: "", description: "", logo_url: "", active: true });
    };
    const cancel = () => { setEditing(null); setError(""); };

    const save = async () => {
        setError("");
        try {
            if (editing === "new") {
                await api.post("/admin/brands", form);
                toast.success("Brand created");
            } else {
                await api.put(`/admin/brands/${editing}`, form);
                toast.success("Brand updated");
            }
            setEditing(null);
            await load();
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
        }
    };
    const remove = async (it) => {
        if (!window.confirm(`Delete ${it.name}?`)) return;
        try {
            await api.delete(`/admin/brands/${it.id}`);
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-brands-page">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Catalog</div>
                    <h1 className="font-display text-3xl font-black uppercase tracking-tight">Brands</h1>
                </div>
                <button onClick={startNew} className="inline-flex items-center gap-2 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-4 py-2.5 rounded-sm hover:bg-[#33C9EA]" data-testid="brand-new">
                    <Plus size={16} /> New
                </button>
            </div>

            {(editing !== null) && (
                <div className="bg-[#18181B] border border-[#27272A] rounded-sm p-4 space-y-3">
                    <div className="grid md:grid-cols-2 gap-3">
                        <Inp label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="brand-name" />
                        <Inp label="Slug (auto)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testid="brand-slug" />
                        <Inp label="Logo URL" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} testid="brand-logo" />
                        <div className="flex items-end">
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#00BCE5]" /> Active
                            </label>
                        </div>
                    </div>
                    <Inp label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="brand-desc" />
                    {error && <div className="text-sm text-[#FF3B30]">{error}</div>}
                    <div className="flex gap-2">
                        <button onClick={save} className="inline-flex items-center gap-2 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-4 py-2 rounded-sm" data-testid="brand-save">
                            <Save size={14} /> Save
                        </button>
                        <button onClick={cancel} className="inline-flex items-center gap-2 border border-[#27272A] text-white px-4 py-2 rounded-sm font-display uppercase tracking-widest font-bold">
                            <X size={14} /> Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-[#18181B] border border-[#27272A] rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#121214] text-xs uppercase tracking-widest font-display text-white/55">
                        <tr>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Slug</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-right"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((b) => (
                            <tr key={b.id} className="border-t border-[#27272A]" data-testid={`brand-row-${b.slug}`}>
                                <td className="px-3 py-2 font-medium text-white">{b.name}</td>
                                <td className="px-3 py-2 text-white/55">{b.slug}</td>
                                <td className="px-3 py-2 text-xs uppercase font-display tracking-widest">
                                    {b.active ? <span className="text-[#34C759]">Active</span> : <span className="text-white/45">Inactive</span>}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <div className="inline-flex gap-2">
                                        <button onClick={() => startEdit(b)} className="p-1.5 border border-[#27272A] rounded-sm text-white/85 hover:border-[#00BCE5] hover:text-[#00BCE5]" data-testid={`brand-edit-${b.slug}`}>
                                            <Edit2 size={14} />
                                        </button>
                                        <button onClick={() => remove(b)} className="p-1.5 border border-[#27272A] rounded-sm text-white/85 hover:border-[#FF3B30] hover:text-[#FF3B30]" data-testid={`brand-delete-${b.slug}`}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={4} className="px-3 py-8 text-center text-white/55">No brands yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Inp({ label, value, onChange, testid }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-1">{label}</label>
            <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#0A0A0B] border border-[#27272A] rounded-sm px-3 py-2 text-white focus:border-[#00BCE5] outline-none" data-testid={testid} />
        </div>
    );
}
